import {
	type DelayAsset,
	type GroupedDelayAsset,
	getDelayAssets,
	getDelayAssetsGrouped,
} from "@/shared/repositories/DelayAssetRepository";
import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import { useUserScope } from "@/setup/scope";

export const useDelayAssets = (onError?: (error: Error) => void) => {
	return useQuery<DelayAsset[], Error>({
		queryKey: ["delayAssets"],
		queryFn: () => getDelayAssets(),
		onError: onError,
	});
};

type Option = { label: string; value: number | null };

type Props = {
	value: number | null;
	onChange: (value: number | null) => void;
	showAllOption?: boolean;
	label?: string;
	required?: boolean;
	disabled?: boolean;
	width?: number | string;
	fieldError?: FieldError | undefined;
	/** Only lines belonging to this area (ParentAssetId) */
	areaAssetId?: number;
};

export const LinesFilter = ({
	value,
	onChange,
	showAllOption = true,
	label = "Line",
	required = false,
	disabled = false,
	width = 160,
	fieldError,
	areaAssetId,
}: Props) => {
	const [error, setError] = useState("");
	const scope = useUserScope();

	const { data: allLines, isLoading: allLoading } = useDelayAssets((error) =>
		setError(error.message),
	);

	const { data: groupedLines, isLoading: groupedLoading } = useQuery<
		GroupedDelayAsset[],
		Error
	>({
		queryKey: ["delayAssetsGrouped"],
		queryFn: () => getDelayAssetsGrouped(),
		enabled: areaAssetId != null,
		onError: (e) => setError(e.message),
	});

	const linesList =
		areaAssetId != null
			? (groupedLines ?? []).filter((l) => l.ParentAssetId === areaAssetId)
			: allLines;
	const isLoading = areaAssetId != null ? groupedLoading : allLoading;

	const lineOptions: Option[] = Array.isArray(linesList)
		? linesList
				.filter((line) => scope.includes(line.AssetId))
				.map((line) => ({
					label: line.AssetName,
					value: line.AssetId,
				}))
		: [];

	// Single line in scope: fixed
	const fixedLine =
		!scope.isFull && lineOptions.length === 1 ? lineOptions[0].value : null;
	useEffect(() => {
		if (fixedLine != null && value !== fixedLine) onChange(fixedLine);
	}, [fixedLine, value]);

	const options: Option[] =
		showAllOption && fixedLine == null
			? [{ label: "All", value: null }, ...lineOptions]
			: lineOptions;

	const selectedOption =
		options.find((option) => option.value === value) ??
		(showAllOption ? options[0] : null);

	return (
		<>
			<Autocomplete
				sx={{ width }}
				value={selectedOption}
				onChange={(_event, newValue) => {
					onChange(newValue ? newValue.value : null);
				}}
				options={options}
				getOptionLabel={(option) => option.label}
				isOptionEqualToValue={(option, value) => option.value === value.value}
				disabled={isLoading || disabled || fixedLine != null}
				disablePortal
				disableClearable={showAllOption}
				renderInput={(params) => (
					<TextField
						{...params}
						label={required ? `${label} *` : label}
						variant="outlined"
						size="small"
						fullWidth
						error={!!fieldError}
						helperText={fieldError?.message}
					/>
				)}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
